"use client";

import { storyblokEditable } from "@storyblok/react";
import Image from "next/image";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { sbBlok } from "@/types/sb-types";
import "swiper/css";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import Cta from "./Cta";
import { NODE_PARAGRAPH, render } from "storyblok-rich-text-react-renderer";

interface InfoCarouselProps {
  blok: sbBlok;
}

const InfoCarousel = ({ blok }: InfoCarouselProps) => {
  return (
    <div className="grid-container">
      <div className="col-start-1 col-span-4 row-start-1 lg:col-start-2 lg:col-span-14 2xl:col-start-2 2xl:col-span-30">
        <Swiper
          {...storyblokEditable(blok)}
          className="my-10 mx-0 lg:my-20"
          modules={[Navigation, Pagination]}
          spaceBetween={50}
          slidesPerView="auto"
          navigation={{
            nextEl: `.swiper-info-carousel-nextEl`,
            prevEl: `.swiper-info-carousel-prevEl`,
          }}
          grabCursor
          centeredSlides
          pagination={{ clickable: true }}
        >
          {blok.content.map((i: sbBlok) => {
            return (
              <SwiperSlide
                key={i._uid}
                className="!flex flex-col !w-full rounded-[10px] overflow-hidden bg-tm-c-gray-700 !h-auto md:flex-row md:!w-[750px] md:!h-[520px]"
              >
                <div className="relative h-[300px] md:h-full md:w-2/5">
                  <Image
                    className="object-cover block lg:hidden"
                    src={i.image_mobile?.filename || i.image.filename}
                    alt={i.image.alt}
                    fill
                    sizes="100vw"
                  />
                  <Image
                    className="object-cover hidden lg:block"
                    src={i.image.filename}
                    alt={i.image.alt}
                    fill
                    sizes="100vw"
                  />
                </div>
                <div className="p-5 text-center md:px-7.5 md:py-14 md:w-[60%] md:text-left">
                  <h2 className="m-0 mb-2.5 text-2xl">{i.title}</h2>
                  {i.content_text &&
                    render(i.content_text, {
                      nodeResolvers: {
                        [NODE_PARAGRAPH]: (children) => (
                          <p className="mt-0 mx-0 mb-5 text-base">{children}</p>
                        ),
                      },
                    })}
                  {i.content.map((c: sbBlok) => {
                    return (
                      <p key={c._uid} className="mt-0 mx-0 mb-5 text-base">
                        {c.content}
                      </p>
                    );
                  })}
                  {(i.cta_link?.url?.length > 0 ||
                    i.cta_link?.story?.slug?.length > 0) && (
                    <Cta
                      className="my-0 mx-auto md:mx-0 md:mt-2.5"
                      name={
                        i.cta_link_name.length > 0
                          ? i.cta_link_name
                          : i.cta_link?.story?.name
                      }
                      href={i.cta_link?.url}
                      fullSlug={i.cta_link?.story?.full_slug}
                    />
                  )}
                </div>
              </SwiperSlide>
            );
          })}
          <div className="swiper-info-carousel-prevEl absolute bottom-1 cursor-pointer">
            <ArrowBackIosNewOutlinedIcon fontSize="small" />
          </div>
          <div className="swiper-info-carousel-nextEl absolute bottom-1 cursor-pointer">
            <ArrowForwardIosOutlinedIcon fontSize="small" />
          </div>
        </Swiper>
      </div>
    </div>
  );
};

export default InfoCarousel;
